<template>
  <div>
    <div id="map"></div>
    <IconMarker
      v-if="iconMarkerVisible"
      :map="map"
      :items="processLocationsDetail"
    />
    <div v-if="supplierMarkerVisible">
      <SupplierMarker3D
        v-if="
          order &&
            order.service &&
            order.service.map_icon_3d &&
            order.service.map_icon_3d.url
        "
        :map-view="map"
        :order="order"
        v-on:updateSupplierLocation="updateSupplierLocation"
        v-on:invalidOrderMode="handleInvalidOrder"
      />
      <SupplierMarker
        v-else
        :map-view="map"
        :order="order"
        v-on:updateSupplierLocation="updateSupplierLocation"
        v-on:invalidOrderMode="handleInvalidOrder"
      />
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet-hotline";
import "leaflet/dist/leaflet.css";
import "leaflet.polyline.snakeanim";
import polyUtil from "polyline-encoded";
import loadSource from "@/mixins/load-source";

const SupplierMarker = () => import("./SupplierMarker");
const SupplierMarker3D = () => import("./SupplierMarker3D");
const IconMarker = () => import(/* webpackPreload: true */ "./IconMarker");

export default {
  name: "MapViews",
  props: {
    isPaddingLeft: Boolean,
    isShowStepsLocation: Boolean,
  },
  mixins: [loadSource],
  components: {
    SupplierMarker,
    SupplierMarker3D,
    IconMarker,
  },
  data: function() {
    var result = {
      publicPath: process.env.BASE_URL,
      map: null,

      polylines: null,
      popup: null,
      pointMarkersLayer: null,
      historyLocationLayer: null,

      markersLayer: null,
      polylineLayer: null,
      matching: null,
      supplierLocation: {},
      locationHistory: [],
      locateInterval: null,
      pointQueue: null,
    };
    return result;
  },
  computed: {
    iconMarkerVisible() {
      return this.map && this.order && this.isShowStepsLocation;
    },
    supplierMarkerVisible: {
      cache: false,
      get() {
        const validStatus = [
          this.$constants.ORDER_STATUS.ACCEPTED,
          this.$constants.ORDER_STATUS.BOARDED,
          this.$constants.ORDER_STATUS.IN_PROCESS,
        ];
        return (
          this.map &&
          this.order &&
          this.order.status &&
          validStatus.indexOf(this.order.status) > -1
        );
      },
    },
    order: {
      cache: false,
      get() {
        return this.$store.state.order_detail;
      },
    },
    supplierLocations: {
      cache: false,
      get() {
        return this.$store.state.supplierLocations;
      },
    },
    processLocationsDetail() {
      const dropOffIcon = `${this.publicPath}img/map/IcLocationDrv.svg`;
      const iconsDetail = [
        {
          url: dropOffIcon,
          desc: this.$t("SHARE_ORDER.PROCESS_LOCATION.ACCEPTED"),
          prefix: "accept",
          distanceField: "accept_distance",
        },
        // {
        // 	url: dropOffIcon,
        // 	desc: this.$t('SHARE_ORDER.PROCESS_LOCATION.BOARDED'),
        // 	prefix: "board",
        // },
        {
          url: dropOffIcon,
          desc: this.$t("SHARE_ORDER.PROCESS_LOCATION.PICKED_UP"),
          prefix: "pickup",
          distanceField: "board_distance",
        },
        {
          url: dropOffIcon,
          desc:
            this.order.status === this.$constants.ORDER_STATUS.COMPLETED
              ? this.$t("SHARE_ORDER.PROCESS_LOCATION.COMPLETED")
              : this.$t("SHARE_ORDER.PROCESS_LOCATION.FAILED"),
          prefix: "complete",
          distanceField: "complete_distance",
        },
        {
          url: dropOffIcon,
          desc: this.$t("SHARE_ORDER.PROCESS_LOCATION.RETURNED"),
          prefix: "return",
        },
      ];

      let processLocations = [];
      iconsDetail.forEach((item) => {
        if (
          this.order[`${item.prefix}_lat`] &&
          this.order[`${item.prefix}_lng`]
        ) {
          item.lat = this.order[`${item.prefix}_lat`];
          item.lng = this.order[`${item.prefix}_lng`];

          if (
            item.distanceField &&
            (this.order[item.distanceField] ||
              this.order[item.distanceField] === 0)
          ) {
            item.desc += ` (${item.distanceField} = ${
              this.order[item.distanceField]
            } km)`;
          }
          processLocations.push(item);
        }
      });
      return processLocations;
    },
    enableRegionBaseMap() {
      return (
        this.$route.query &&
        this.$route.query.region_basemap &&
        this.$route.query.region_basemap === "true"
      );
    },
  },
  methods: {
    async initMapView() {
      const path = this.order.path;
      const origin = path[0];
      const firstDropoff = path[1];
      const originLat =
        (origin && origin.lat) || (firstDropoff && firstDropoff.lat);
      const originlng =
        (origin && origin.lng) || (firstDropoff && firstDropoff.lng);

      if (this.map || !(originLat && originlng)) return;
      const center = [originLat, originlng];
      const mapViewType = process.env.VUE_APP_MAPVIEW_TYPE;

      console.log("initMapView", this.order);
      this.map = L.map("map", {
        zoomControl: false,
        attributionControl: false,
      }).setView(center, 17);

      switch (mapViewType) {
        case "mapbox": {
          await import("mapbox-gl-leaflet");
          L.mapboxGL({
            accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
            style: "mapbox://styles/mapbox/outdoors-v9?optimize=true",
          }).addTo(this.map);
          break;
        }
        case "google": {
          await this.loadGoogleMapApiJS();
          await import("leaflet.gridlayer.googlemutant");

          const tch_style = [
            {
              featureType: "administrative",
              elementType: "labels.text.fill",
              stylers: [{ color: "#6195a0" }],
            },
            {
              featureType: "administrative.province",
              elementType: "geometry.stroke",
              stylers: [{ visibility: "off" }],
            },
            {
              featureType: "landscape",
              elementType: "geometry",
              stylers: [
                { color: "#f5f5f2" },
                { saturation: "0" },
                { lightness: "0" },
                { gamma: "1" },
              ],
            },
            {
              featureType: "landscape.man_made",
              stylers: [{ lightness: "-3" }, { gamma: "1.00" }],
            },
            {
              featureType: "landscape.natural.terrain",
              stylers: [{ visibility: "off" }],
            },
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            {
              featureType: "poi.park",
              elementType: "geometry.fill",
              stylers: [{ color: "#bae5ce" }, { visibility: "on" }],
            },
            {
              featureType: "road",
              stylers: [
                { saturation: -100 },
                { lightness: 45 },
                { visibility: "simplified" },
              ],
            },
            {
              featureType: "road.arterial",
              elementType: "labels.icon",
              stylers: [{ visibility: "off" }],
            },
            {
              featureType: "road.arterial",
              elementType: "labels.text.fill",
              stylers: [{ color: "#787878" }],
            },
            {
              featureType: "road.highway",
              stylers: [{ visibility: "simplified" }],
            },
            {
              featureType: "road.highway",
              elementType: "geometry.fill",
              stylers: [{ color: "#fac9a9" }, { visibility: "simplified" }],
            },
            {
              featureType: "road.highway",
              elementType: "labels.text",
              stylers: [{ color: "#4e4e4e" }],
            },
            { featureType: "transit", stylers: [{ visibility: "simplified" }] },
            {
              featureType: "transit.station.airport",
              elementType: "labels.icon",
              stylers: [
                { hue: "#0a00ff" },
                { saturation: "-77" },
                { lightness: "0" },
                { gamma: "0.57" },
              ],
            },
            {
              featureType: "transit.station.rail",
              elementType: "labels.icon",
              stylers: [
                { hue: "#ff6c00" },
                { saturation: "-68" },
                { lightness: "4" },
                { gamma: "0.75" },
              ],
            },
            {
              featureType: "transit.station.rail",
              elementType: "labels.text.fill",
              stylers: [{ color: "#43321e" }],
            },
            {
              featureType: "water",
              stylers: [{ color: "#eaf6f8" }, { visibility: "on" }],
            },
            {
              featureType: "water",
              elementType: "geometry.fill",
              stylers: [{ color: "#c7eced" }],
            },
            {
              featureType: "water",
              elementType: "labels.text.fill",
              stylers: [
                { saturation: "-53" },
                { lightness: "-49" },
                { gamma: "0.79" },
              ],
            },
          ];
          L.gridLayer
            .googleMutant({
              type: "roadmap",
              styles: tch_style,
            })
            .addTo(this.map);
          break;
        }
        default: {
          await import("mapbox-gl-leaflet");
          // Raster
          // L.tileLayer(`${process.env.VUE_APP_AHAMOVE_MAP_API}/styles/basic/{z}/{x}/{y}.png`, {
          // 	maxZoom: 20
          // }).addTo(this.map)

          // Vector
          L.mapboxGL({
            accessToken: "no-token",
            container: "map",
            // style: 'http://localhost:8080/styles/basic/style.json',
            style: `${
              this.enableRegionBaseMap
                ? process.env.VUE_APP_AHAMOVE_REGION_BASEMAP_API
                : process.env.VUE_APP_AHAMOVE_MAP_API
            }/styles/basic/style.json`,
          }).addTo(this.map);

          const credits = L.control
            .attribution({
              prefix:
                '<a href="https://ahamove.com/" target="_blank">Powered by AhaMove</a>',
              position: "bottomright",
            })
            .addTo(this.map);
          credits.addAttribution(
            '<a href="https://www.openmaptiles.org/" target="_blank">&copy; OpenMapTiles</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
          );
          break;
        }
      }

      L.control
        .zoom({
          position: "topright",
        })
        .addTo(this.map);

      this.map.on(
        "load",
        function() {
          console.log("loaded", this.map);
        }.bind(this)
      );
    },
    drawDirectionPolyline() {
      // if (this.map) {
      if (this.polylines && this.map.hasLayer(this.polylines)) {
        this.map.removeLayer(this.polylines);
        delete this.polylines;
      }

      const polylinesData = this.order && this.order.polylines;
      if (polylinesData) {
        var pointList = polyUtil.decode(polylinesData);

        if (pointList && pointList.length > 0) {
          const coors = pointList.map((p, index) => {
            return [p[0], p[1], index];
          });

          const length = coors.length;
          let polylinesData = L.hotline(coors, {
            palette: {
              0: "#5177FF",
              1: "#FF6838",
            },
            max: length,
            outlineWidth: 0,
          });

          this.polylines = polylinesData;
          setTimeout(() => {
            this.map.addLayer(this.polylines);
          }, 1000);
        }
      }
      // }
    },
    drawSupplierLocationsPolyline() {
      console.log("drawSupplierLocationsPolyline", this.supplierLocations);

      if (!this.supplierLocations || this.supplierLocations.length === 0) {
        return;
      }

      if (
        this.supplierLocationsPolylines &&
        this.map.hasLayer(this.supplierLocationsPolylines)
      ) {
        this.map.removeLayer(this.supplierLocationsPolylines);
        delete this.supplierLocationsPolylines;
      }

      var pointList = [];
      for (let i = 0; i < this.supplierLocations.length; i++) {
        const p = L.latLng(
          this.supplierLocations[i].lat,
          this.supplierLocations[i].lng
        );
        pointList.push(p);
      }

      if (pointList && pointList.length > 0) {
        try {
          // this.supplierLocationsPolylinesBorder = new L.Polyline(pointList,{
          // 	color: '#f37336',
          // 	weight: 4,
          // 	opacity: 0.8,
          // 	smoothFactor: 0,
          // 	noClip: true,
          // 	interactive: false,
          // 	bubblingMouseEvents: false,
          // 	lineJoin: 'round',
          // 	className: 'direction-polyline',
          // })
          // this.map.addLayer(this.supplierLocationsPolylinesBorder)

          this.supplierLocationsPolylines = new L.Polyline(pointList, {
            color: "#ff8d00eb",
            weight: 2,
            opacity: 0.96,
            smoothFactor: 0,
            noClip: true,
            interactive: false,
            bubblingMouseEvents: false,
            lineJoin: "arcs",
            className: "direction-polyline",
            pulseColor: "#f18552",
            dashArray: "6, 6",
            dashOffset: "0",
          });
          this.map.addLayer(this.supplierLocationsPolylines);

          this.historyLocationLayer = L.layerGroup();
          const total = pointList.length;
          pointList.forEach((point, index) => {
            const row = this.supplierLocations[index];
            const time = this.$moment(row.track_time.value).format(
              "HH:mm DD/MM"
            );
            const label = `[${index + 1}/${total}] ${time}`;
            L.circleMarker(point, {
              radius: 5,
              weight: 3,
              color: "#f18552",
              fillColor: "#ffffff",
              fillOpacity: 0.96,
              title: row.track_time.value,
            })
              .bindTooltip(label, {
                permanent: false,
                direction: "top",
              })
              .addTo(this.historyLocationLayer);
          });
          this.map.addLayer(this.historyLocationLayer);
        } catch (e) {
          console.log("e", e);
        }
      }
    },
    drawPointMarkers() {
      if (this.map) {
        if (
          this.pointMarkersLayer &&
          this.map.hasLayer(this.pointMarkersLayer)
        ) {
          // this.map.removeLayer(this.pointMarkersLayer)
          return;
        }

        this.pointMarkersLayer = L.layerGroup();
        const enabledPath = this.order.path;
        const addressLabelVisible = enabledPath.length === 2;
        const isMultipleDropoffs = enabledPath && enabledPath.length > 2;

        const pickupPointSvg = require(`!svg-inline-loader?idPrefix!@/../public/img/map/PickupPointMarker.svg`);
        const dropoffPointSvg = require(`!svg-inline-loader?idPrefix!@/../public/img/map/DropoffPointMarker.svg`);
        const stopPointSvg = require(`!svg-inline-loader?idPrefix!@/../public/img/map/StopPointMarker.svg`);
        // const completedStopPoint = require(`!svg-inline-loader?idPrefix!./../../../public/img/map/DropoffPointMarker.svg`);
        const failedStopPoint = require(`!svg-inline-loader?idPrefix!@/../public/img/map/FailedStopPoint.svg`);

        enabledPath.forEach((point, index) => {
          if (point.lat && point.lng) {
            const center = L.latLng(point.lat, point.lng);
            let iconSize = [48, 48];
            let iconAnchor = [24, 48];

            let iconUrl = pickupPointSvg;
            if (index > 0) {
              if (isMultipleDropoffs) {
                iconUrl = stopPointSvg.replace("{number}", index);
                if (index > 9) {
                  iconUrl = iconUrl.replace(`x="11.4"`, `x="9"`);
                }
              } else iconUrl = dropoffPointSvg;
            }

            if (point.status) {
              // TODO update completedStopPoint
              // if (point.status === this.$constants.ORDER_STATUS.COMPLETED) {
              // 	iconUrl = completedStopPoint
              // }
              if (point.status === this.$constants.ORDER_STATUS.FAILED) {
                iconUrl = failedStopPoint;
              }
            }

            let iconSettings = {
              mapIconUrl: iconUrl,
            };
            let divIcon = L.divIcon({
              className: "aha-point-marker",
              html: L.Util.template(iconSettings.mapIconUrl, iconSettings),
              iconAnchor: iconAnchor,
              iconSize: iconSize,
              popupAnchor: [-3, -76],
            });
            const options = {
              icon: divIcon,
              title: point.address,
              riseOnHover: true,
              zIndexOffset: 40,
            };

            if (addressLabelVisible) {
              let popupContent = `<p class="address">${point.address}</p>`;
              let markerPopup = L.popup({
                closeButton: false,
                autoClose: false,
                closeOnClick: false,
                className: "marker-popup",
              })
                .setLatLng(center)
                .setContent(popupContent)
                .addTo(this.pointMarkersLayer);
              L.marker(center, options)
                .addTo(this.pointMarkersLayer)
                .on("click", () => {
                  markerPopup.openOn(this.map);
                });
            }

            L.marker(center, options).addTo(this.pointMarkersLayer);
          }
        });

        this.map.addLayer(this.pointMarkersLayer);
      }
    },
    fitMapBound() {
      if (this.map) {
        const paddingLeft = this.isPaddingLeft ? 500 : 40;
        let fitBoundsOptions = {
          paddingTopLeft: [paddingLeft, 40],
          paddingBottomRight: [40, 40],
          maxZoom: 14,
        };

        if (window && window.screen.width < 769) {
          if (window.screen.height > 360) {
            fitBoundsOptions = {
              paddingTopLeft: [40, 40],
              paddingBottomRight: [80, 40],
            };
          } else {
            fitBoundsOptions = {
              paddingTopLeft: [40, 40],
              paddingBottomRight: [80, 40],
            };
          }
        }
        if (this.polylines) {
          try {
            const bounds = this.polylines.getLatLngs();
            this.map.fitBounds(bounds, fitBoundsOptions);
          } catch (e) {
            console.log("e", e);
          }
        } else if (this.pointMarkersLayer) {
          const enabledPath = this.order.path;
          const latLngs = [];
          if (enabledPath && enabledPath.length > 1) {
            try {
              enabledPath.forEach((point) => {
                const center = L.latLng(point.lat, point.lng);
                latLngs.push(center);
              });
              const bounds = L.latLngBounds(latLngs);
              this.map.fitBounds(bounds, fitBoundsOptions);
            } catch (e) {
              console.log("e", e);
            }
          }
        } else if (
          this.pickupLocation &&
          this.pickupLocation.lng &&
          this.pickupLocation.lat
        ) {
          const center = L.latLng(
            this.pickupLocation.lat,
            this.pickupLocation.lng
          );
          this.map.setView(center, 17);
        }
      }
    },
    updateSupplierLocation(latlng) {
      this.supplierLocation = latlng;
      // console.log('updateSupplierLocation', JSON.stringify(latlng), new Date())
    },
    handleInvalidOrder() {},
    visualizeExtraInfo() {
      // accept_lat, accept_lng, accept_time, accept_distance, accept_duration
      // board_lat, board_lng, board_time, board_distance, board_duration
      // pickup_lat, pickup_lng, pickup_time
      // complete_lat, complete_lng, complete_time, complete_distance, complete_duration
    },
  },
  mounted() {
    this.initMapView();
    this.drawPointMarkers();

    const query = this.$route.query;
    if (query && query.process_location == "true") {
      this.drawDirectionPolyline();
    }

    this.drawSupplierLocationsPolyline();
    this.fitMapBound();
  },
  watch: {
    order: function() {
      this.drawPointMarkers();
    },
    supplierLocations: function(val, oldVal) {
      if (val.length !== oldVal.length) {
        this.drawSupplierLocationsPolyline();
      }
    },
  },
};
</script>

<style lang="scss">
#map {
  position: fixed;
  top: 0;
  left: 0;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100vh;

  .leaflet-control-zoom {
    margin-right: 16px !important;
    margin-top: 68px !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.42);
    border-radius: 4px;
    border: none !important;

    .leaflet-control-zoom-in {
      border-radius: 4px 4px 0 0 !important;
    }

    .leaflet-control-zoom-out {
      border-radius: 0 0 4px 4px !important;
    }

    .leaflet-control-zoom-in,
    .leaflet-control-zoom-out {
      width: 40px !important;
      height: 32px !important;
      font-size: 18px;
      color: #7a8189;
    }
  }

  .distance-popup {
    .leaflet-popup-content-wrapper {
      background: #333;
      color: #ffffff;
      border-radius: 2px;

      .leaflet-popup-content {
        margin: 0;

        p {
          padding: 5px;
          margin: 0;
        }
      }
    }
    .leaflet-popup-tip-container {
      left: 20%;

      .leaflet-popup-tip {
        background: #333;
        color: #ffffff;
        width: 14px;
        height: 14px;
      }
    }
  }

  .marker-popup {
    margin-bottom: 58px;

    .leaflet-popup-content-wrapper {
      background: transparent !important;
      color: #333;
      border-radius: 2px;
      padding-right: 10px;
      box-shadow: none;
      margin-left: -85px;

      .leaflet-popup-content {
        margin: 0;
        padding: 7px;
        width: 135px !important;
        line-height: 1;
        color: #333;
        background: rgba(255, 255, 255, 0.94);
        color: #333;
        border-radius: 6px;
        box-shadow: 1px 1px 3px 0px rgba(68, 68, 68, 0.53);

        p {
          padding: 0;
          margin: 0;
          line-height: 1;
          vertical-align: middle;

          &.address {
            display: inline-block;
            width: 123px;
            white-space: nowrap;
            overflow: hidden !important;
            text-overflow: ellipsis;
            letter-spacing: -0.2px;
            font-weight: 500;
          }

          &.name,
          &.mobile {
            font-weight: 500;
          }
        }
      }
    }

    .leaflet-popup-tip-container {
      left: 50%;
      display: none;

      .leaflet-popup-tip {
        background: #ffffff;
        color: #333333;
        width: 14px;
        height: 14px;
      }
    }

    .address {
      padding-right: 10px;
    }
  }
}

.leaflet-tile {
  text-align: left !important;
}

.aha-point-marker {
  svg {
    text {
      font-size: 9px;
      font-weight: 500;
    }
  }
}
@media only screen and (max-width: 768px) {
  #map {
    position: relative;
    height: 65vh;
  }
}
</style>
